import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, VIEW, ADD, EDIT } from "const"
import { OkrSession, OkrObjective, OkrObjectiveCreate, OkrKeyResult } from "server/models"
import { okrSessionsRoute } from "client/parts/routes"
import {
    StateType,
    useInput,
    replaceHistoryState,
    hasAccess,
} from "client/utils"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,
    OkrObjectiveObjectProps,
    OkrObjectiveValidation,

    SaveOkrObjective,
    LoadOkrObjectiveKeyResults,
    ClearParentsOkrObjective,
    MoveOkrKeyResult,
    IsOwnerOkrObjective,
    IsOwnerOkrKeyResult,

    setOkrObjectiveEdit,
    setOkrObjectiveReloadList,
    setOkrObjectiveReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownSearch,
    SelectDropdownItem,
    Empty,
    Input,
    Textarea,
    Feedback,
    UserDropdownList,
    TagEditor,
    Delimiter,
    Button,
    SpinnerIcon,
} from "client/components"

let isMounted: boolean

const Form = ({
    objective,
}: OkrObjectiveObjectProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const objectiveRoute = useSelector((state: StoreType) => state.okrObjective.objectiveRoute)
    const availableSessions = useSelector((state: StoreType) => state.okrObjective.availableSessions)
    const availableObjectives = useSelector((state: StoreType) => state.okrObjective.availableObjectives)
    const availableKeyResults = useSelector((state: StoreType) => state.okrObjective.availableKeyResults)

    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [parentSession, setParentSession] = useState(objective.session || session)
    const [sessionSearch, setSessionSearch] = useState("")
    const [sessionsFiltered, setSessionsFiltered] = useState<OkrSession[]>([])

    const [parentObjective, setParentObjective] = useState(objective.objective || null)
    const [objectiveSearch, setObjectiveSearch] = useState("")
    const [objectivesFiltered, setObjectivesFiltered] = useState<OkrObjective[]>([])

    const [parentKeyResult, setParentKeyResult] = useState(objective.keyResult || null)
    const [keyResultSearch, setKeyResultSearch] = useState("")
    const [keyResultsFiltered, setKeyResultsFiltered] = useState<OkrKeyResult[]>([])

    const [title, setTitle] = useInput(objective.title)
    const [description, setDescription] = useInput(objective.description)
    const [owners, setOwners] = useState<number[]>([])
    const [tags, setTags] = useState<number[]>([])

    const [validation, setValidation] = useState<OkrObjectiveValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    const addAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, ADD)
    const editAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, EDIT)

    useEffect(() => {
        isMounted = true
        if (objective.id) {
            if (objective.owners) {
                const userIds: number[] = []
                objective.owners.forEach((row) => {
                    userIds.push(row.userId)
                })
                setOwners(userIds)
            }
            if (objective.tags) {
                const tagIds: number[] = []
                objective.tags.forEach((row) => {
                    tagIds.push(row.tagId)
                })
                setTags(tagIds)
            }
        } else {
            setOwners([userData.userId])
        }
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const filtered = availableSessions.filter((row) => (
            row.name.toUpperCase().indexOf(sessionSearch.trim().toUpperCase()) >= 0
        ))
        setSessionsFiltered(filtered)
    }, [availableSessions, sessionSearch])

    useEffect(() => {
        if (!parentSession) return
        const objectives = availableObjectives[parentSession.id] || []
        const filtered = objectives.filter((row) => (
            row.title.toUpperCase().indexOf(objectiveSearch.trim().toUpperCase()) >= 0
        ))
        setObjectivesFiltered(filtered)
    }, [parentSession, availableObjectives, objectiveSearch])

    useEffect(() => {
        if (!parentSession) return
        const keyResults = availableKeyResults[parentSession.id] || []
        const filtered = keyResults.filter((row) => (
            row.title.toUpperCase().indexOf(keyResultSearch.trim().toUpperCase()) >= 0
        ))
        setKeyResultsFiltered(filtered)
    }, [parentSession, availableKeyResults, keyResultSearch])

    const onClose = () => {
        // this page can be opened only via modal
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (!owners.length) return

        // check if we can save this objective as child of a target objective/KR
        let accessToAddChildObjective = false
        const isOwnerParentSession = parentSession.owner === userData.userId
        if (parentObjective) {
            const isOwnerParentObjective = IsOwnerOkrObjective(parentObjective, userData.userId)
            accessToAddChildObjective = (addAccess && editAccess) || isOwnerParentObjective || isOwnerParentSession
            if (!accessToAddChildObjective) {
                setValidation({ objectiveId: lang.noAccessToAddObjective })
                return
            }
        } else if (parentKeyResult) {
            const isOwnerParentKeyResult = IsOwnerOkrKeyResult(parentKeyResult, userData.userId)
            accessToAddChildObjective = (addAccess && editAccess) || isOwnerParentKeyResult || isOwnerParentSession
            if (!accessToAddChildObjective) {
                setValidation({ keyResultId: lang.noAccessToAddObjective })
                return
            }
        } else {
            accessToAddChildObjective = (addAccess && editAccess) || isOwnerParentSession
            if (!accessToAddChildObjective) {
                setValidation({ sessionId: lang.noAccessToAddObjective })
                return
            }
        }

        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newObjectiveData: OkrObjectiveCreate = {
            sessionId: parentSession ? parentSession.id : null,
            objectiveId: parentObjective ? parentObjective.id : null,
            keyResultId: parentKeyResult ? parentKeyResult.id : null,
            title,
            description,
        }

        SaveOkrObjective(
            objective.id,
            newObjectiveData,
            owners,
            tags,
            lang,
        ).then((res) => {
            if (res.validation && isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
                return
            }

            // since we can change parent session, we also need to update it in the url
            const objectiveBaseRoute = `${okrSessionsRoute}/${VIEW}/${parentSession && parentSession.id}${objectiveRoute}`
            if (res.newObjectiveId && isMounted) replaceHistoryState(`${objectiveBaseRoute}/${EDIT}/${res.newObjectiveId}`)

            // if we changed parent session for existing objective
            if (objective.id && objective.sessionId !== newObjectiveData.sessionId) {
                const oldSessionId = objective.sessionId
                const newSessionId = newObjectiveData.sessionId

                Promise.all([
                    ClearParentsOkrObjective({
                        sessionId: oldSessionId,
                        objectiveId: objective.id,
                        keyResultId: null,
                    }),
                    LoadOkrObjectiveKeyResults(objective.id, oldSessionId).then((res1) => {
                        const promiseArray: Promise<any>[] = []
                        Object.values(res1.keyResultList).forEach((keyResult: OkrKeyResult) => {
                            promiseArray.push(
                                MoveOkrKeyResult({
                                    id: keyResult.id,
                                    sessionId: newSessionId,
                                }),
                                ClearParentsOkrObjective({
                                    sessionId: oldSessionId,
                                    objectiveId: null,
                                    keyResultId: keyResult.id,
                                }),
                            )
                        })
                        return Promise.all(promiseArray)
                    }),
                ]).then(() => {
                    // to prevent issues with updating urls when change parent session - just reload the page
                    if (mode === 1) window.location.href = `${objectiveBaseRoute}/${VIEW}/${objective.id}`
                    if (mode === 2) window.location.href = `${objectiveBaseRoute}/${EDIT}/${objective.id}`
                })
            } else {
                if (isMounted) {
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }

                dispatch(setOkrObjectiveEdit({
                    id: res.newObjectiveId || objective.id,
                    ...newObjectiveData,
                    session: parentSession,
                    objective: parentObjective,
                    keyResult: parentKeyResult,
                    owners: res.owners,
                    tags: res.tags,
                }))

                dispatch(setOkrObjectiveReloadList(false))
                setTimeout(() => {
                    dispatch(setOkrObjectiveReloadList(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setOkrObjectiveReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setOkrObjectiveReloadEdit(true))
                    }, 0)
                }
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.session}</Label>
                        <SelectDropdown title={parentSession ? parentSession.name : <Empty />}>
                            <SelectDropdownSearch
                                value={sessionSearch}
                                onChange={(e) => setSessionSearch(e.target.value)}
                                onClearSearch={() => setSessionSearch("")}
                            />
                            {
                                sessionsFiltered.map((row) => (
                                    <SelectDropdownItem
                                        key={row.id}
                                        onClick={() => {
                                            setParentSession(row)
                                            setParentObjective(null)
                                            setParentKeyResult(null)
                                        }}
                                    >
                                        {row.name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.sessionId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.parentObjective}</Label>
                        <SelectDropdown title={parentObjective ? parentObjective.title : <Empty />}>
                            <SelectDropdownSearch
                                value={objectiveSearch}
                                onChange={(e) => setObjectiveSearch(e.target.value)}
                                onClearSearch={() => setObjectiveSearch("")}
                            />
                            <SelectDropdownItem onClick={() => setParentObjective(null)}>
                                <Empty />
                            </SelectDropdownItem>
                            {
                                objectivesFiltered.map((row) => (
                                    <SelectDropdownItem
                                        key={row.id}
                                        onClick={() => {
                                            setParentObjective(row)
                                            setParentKeyResult(null)
                                        }}
                                    >
                                        {row.title}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.objectiveId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.parentKeyResult}</Label>
                        <SelectDropdown title={parentKeyResult ? parentKeyResult.title : <Empty />}>
                            <SelectDropdownSearch
                                value={keyResultSearch}
                                onChange={(e) => setKeyResultSearch(e.target.value)}
                                onClearSearch={() => setKeyResultSearch("")}
                            />
                            <SelectDropdownItem onClick={() => setParentKeyResult(null)}>
                                <Empty />
                            </SelectDropdownItem>
                            {
                                keyResultsFiltered.map((row) => (
                                    <SelectDropdownItem
                                        key={row.id}
                                        onClick={() => {
                                            setParentObjective(null)
                                            setParentKeyResult(row)
                                        }}
                                    >
                                        {row.title}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.keyResultId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.title}</Label>
                        <Input type="text" value={title} onChange={setTitle} />
                        <Feedback>{validation && validation.title}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.owners}</Label>
                        <UserDropdownList
                            selectedIds={owners}
                            listIds={(accountUserList && accountUserList[userData.accountId]) || []}
                            title={lang.addOwners}
                            onDelete={setOwners}
                            onAdd={setOwners}
                        />
                        <Feedback>{owners.length === 0 ? lang.validationFieldRequired : null}</Feedback>
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.tags}</Label>
                        <TagEditor
                            selectedIds={tags}
                            onChange={(tagIds) => setTags(tagIds)}
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
